import { useEffect, useRef } from "react";
import { useAuth } from "@/lib/auth";
import { Navigate, useLocation } from "react-router-dom";
import { toast } from "sonner";

/**
 * Route guard.
 *
 * Props:
 *   - children: the page component to render when access is granted.
 *   - requireModule: (optional) module key from MODULE_PERMISSION_MAP.
 *   - requirePermission: (optional) explicit permission key.
 *
 * When invoked WITHOUT `requireModule` and WITHOUT `requirePermission`,
 * this component enforces authentication only.
 *
 * Sprint 1B-B US 1.9-B decision order (strict; first-match wins):
 *   1. loading (auth init not complete)            → loading screen
 *   2. !user                                       → /login
 *   3. !gated                                      → render children
 *   4. permissionState === "idle" | "loading"      → loading screen
 *   5. resolver-failure branch (network/5xx/429/401/403 with permission
 *      state not usable to authorize this route)   → /access-denied
 *                                                    variant=resolver_failure
 *   6. permissionState === "ready" && !can()       → /access-denied
 *                                                    variant=denied
 *   7. otherwise                                   → render children
 */
export default function ProtectedRoute({ children, requireModule, requirePermission }) {
  const auth = useAuth() || {};
  const {
    user,
    loading,
    permissionsLoaded,
    can,
    canSeeModule,
    permissionState,
    permissionResolverError,
    permissionsFresh,
  } = auth;
  const location = useLocation();

  const gated = Boolean(requireModule || requirePermission);

  // 1. Auth initialization still loading.
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6]">
        <div className="text-[#1E3A8A] hw-eyebrow">Loading…</div>
      </div>
    );
  }
  // 2. Unauthenticated.
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  // 3. Ungated route.
  if (!gated) return children;

  // 4. Permission state not yet observable — show spinner.
  //    permissionsLoaded === false iff state ∈ {"idle","loading"}. This
  //    keeps rows 5–7 unreachable ONLY during the initial resolve; once
  //    the resolver has emitted any terminal state (ready/refreshing/error)
  //    the loading screen no longer applies.
  if (gated && permissionsLoaded === false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F9F8F6]">
        <div className="text-[#1E3A8A] hw-eyebrow">Loading…</div>
      </div>
    );
  }

  // 5. Resolver-failure branch — permission state is not usable AND the
  //    resolver has recorded a failure class. Stale-fresh grace: if the
  //    resolver is "refreshing" but permissionsFresh is still true, fall
  //    through to row 6/7 to evaluate the currently-fresh set.
  const stillFreshGrace =
    permissionState === "refreshing" && permissionsFresh === true;
  const isResolverFailure =
    !stillFreshGrace &&
    (permissionState === "error" ||
      (permissionState === "refreshing" && permissionsFresh === false)) &&
    !!permissionResolverError;
  if (isResolverFailure) {
    return (
      <ResolverFailureRedirect
        requirePermission={requirePermission}
        requireModule={requireModule}
        resolverError={permissionResolverError}
      />
    );
  }

  // 6. Ready (or stillFresh-grace refreshing) — evaluate the permission set.
  const ok = requirePermission
    ? (typeof can === "function" && can(requirePermission))
    : (typeof canSeeModule === "function" && canSeeModule(requireModule));
  if (!ok) {
    return (
      <UnauthorizedRedirect
        requirePermission={requirePermission}
        requireModule={requireModule}
      />
    );
  }

  // 7. Authorized.
  return children;
}

function UnauthorizedRedirect({ requirePermission, requireModule }) {
  const location = useLocation();
  const toastedRef = useRef(false);

  useEffect(() => {
    if (!toastedRef.current) {
      toast.error("You don't have permission to access that page.");
      toastedRef.current = true;
    }
  }, []);

  return (
    <Navigate
      to="/access-denied"
      replace
      state={{
        from: location.pathname + (location.search || "") + (location.hash || ""),
        requirePermission: requirePermission || undefined,
        requireModule: requireModule || undefined,
      }}
    />
  );
}

function ResolverFailureRedirect({ requirePermission, requireModule, resolverError }) {
  const location = useLocation();
  return (
    <Navigate
      to="/access-denied"
      replace
      state={{
        from: location.pathname + (location.search || "") + (location.hash || ""),
        variant: "resolver_failure",
        resolverError: resolverError || null,
        // Deliberately omit requirePermission / requireModule for
        // resolver-failure variants: the resolver did not succeed, so no
        // claim about a specific missing permission is meaningful.
        requirePermission:
          resolverError === "401" || resolverError === "403" ? undefined : undefined,
        requireModule:
          resolverError === "401" || resolverError === "403" ? undefined : undefined,
      }}
    />
  );
}
